import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { parseDate } from '@databases/utils/database/field-types/date-normalization';
import { makeValidHyperlink } from '@databases/utils/database/field-types/hyperlink-normalization';
import { createElement, type ReactNode } from 'react';

import { defineFieldReadModule, type FieldReadModule } from '../field-read-module.ts';
import { EMPTY_TEXT_CONTENT, type TextReadContent } from './text-read-content.ts';
import { TextValueView } from './text-value-view.tsx';

type CompiledSelectOption = {
  readonly key: string;
  readonly label: string;
};

function compileSelectOptions(field: DatabaseField): readonly CompiledSelectOption[] {
  return Object.freeze(
    field.settings.selectOptions.map((option) =>
      Object.freeze({
        key: option.id ?? option.label,
        label: option.label
      })
    )
  );
}

function resolveHyperlinkText(
  value: DatabaseValue<DatabaseFieldType.HYPERLINK>['value']
): string {
  return value
    .map(makeValidHyperlink)
    .map((hyperlink) => hyperlink.label ?? hyperlink.url)
    .join('\n');
}

function createTextContent(text: string): TextReadContent {
  return text
    ? Object.freeze<TextReadContent>({ kind: 'text', text })
    : EMPTY_TEXT_CONTENT;
}

export type TextReadRenderer = (content: TextReadContent) => ReactNode;

const renderTextValue: TextReadRenderer = (content) =>
  createElement(TextValueView, { content });

/**
 * Pairs the plain Text projection with a consumer's typed presenter at composition.
 * Resolution remains identical when another view replaces presentation.
 */
export function createTextFieldReadModule({
  render = renderTextValue
}: { readonly render?: TextReadRenderer } = {}): FieldReadModule<TextReadContent> {
  return defineFieldReadModule<TextReadContent>({
    fieldType: DatabaseFieldType.TEXT,
    render,
    compile(field) {
      const selectOptions = compileSelectOptions(field);

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ): TextReadContent {
          const value = storedValue ?? defaultValue;

          switch (value?.type) {
            case DatabaseFieldType.TEXT:
              return createTextContent(value.value.plain);
            case DatabaseFieldType.NUMBER:
              return createTextContent(value.value);
            case DatabaseFieldType.DATE:
              return createTextContent(parseDate(value.value) ?? '');
            case DatabaseFieldType.SELECT: {
              const selected = new Set(value.value);

              return createTextContent(
                selectOptions
                  .filter((option) => selected.has(option.key))
                  .map((option) => option.label)
                  .join('\n')
              );
            }
            case DatabaseFieldType.HYPERLINK:
              return createTextContent(resolveHyperlinkText(value.value));
            default:
              return EMPTY_TEXT_CONTENT;
          }
        }
      });
    }
  });
}

export const textFieldReadModule = createTextFieldReadModule();
